import React, { useState } from 'react';
import SalaryStructureForm from './SalaryStructureForm.jsx';
import { formatDate } from '../../../../lib/employeeHelpers';
import AdvancedFormulaManager from './AdvancedFormulaManager.jsx';

const SalaryStructureManager = ({ salaryComponents, onComponentUpdate, organizationalLevels = {}, statutoryRates = {} }) => {
  const [structures, setStructures] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingStructure, setEditingStructure] = useState(null);
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showFormulaManager, setShowFormulaManager] = useState(false);

  // Use components from props instead of fetching independently
  const savedComponents = salaryComponents || [];
  const loading = false; // No loading since we're using props

  const handleCreateStructure = () => {
    // Refresh saved components to include any newly added ones
    if (onComponentUpdate) {
      onComponentUpdate();
    }
    setShowCreateForm(true);
    setEditingStructure(null);
  };

  const handleCloseForm = () => {
    setShowCreateForm(false);
    setEditingStructure(null);
  };

  const handleSaveStructure = (structureData) => {
    console.log('Saving structure:', structureData);
    // Add the new structure to the list
    const newStructure = {
      id: Date.now(), // Simple ID generation
      ...structureData,
      active: true,
      createdAt: new Date().toISOString()
    };
    setStructures(prev => [...prev, newStructure]);
    setShowCreateForm(false);
  };

  const handleMenuToggle = (id) => {
    setOpenMenuId(openMenuId === id ? null : id);
  };

  const handleMenuAction = (action, id, name) => {
    setOpenMenuId(null);
    switch (action) {
      case 'edit': {
        const structureToEdit = structures.find(structure => structure.id === id);
        setEditingStructure(structureToEdit);
        // Refresh saved components to include any newly added ones
        if (onComponentUpdate) {
          onComponentUpdate();
        }
        setShowCreateForm(true);
        break;
      }
      case 'deactivate':
        if (window.confirm(`Are you sure you want to deactivate "${name}"?`)) {
          setStructures(prev => 
            prev.map(structure => 
              structure.id === id 
                ? { ...structure, active: false, deactivatedAt: new Date().toISOString() }
                : structure
            )
          );
        }
        break;
      case 'reactivate':
        if (window.confirm(`Are you sure you want to reactivate "${name}"?`)) {
          setStructures(prev => 
            prev.map(structure => 
              structure.id === id 
                ? { ...structure, active: true, reactivatedAt: new Date().toISOString() }
                : structure
            )
          );
        }
        break;
      case 'delete':
        if (window.confirm(`Are you sure you want to permanently delete "${name}"? This action cannot be undone.`)) {
          setStructures(prev => prev.filter(structure => structure.id !== id));
        }
        break;
      default:
        break;
    }
  };

  // Filter structures based on showDeactivated
  const filteredStructures = showDeactivated 
    ? structures 
    : structures.filter(structure => structure.active);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Salary Structures
            </h2>
            <p className="text-sm text-gray-600">
              Create and manage salary structures using your components
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {/* Show/Hide Deactivated Toggle Button */}
            <button
              onClick={() => setShowDeactivated(!showDeactivated)}
              className={`inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-redhalo-red/35 transition-colors ${
                showDeactivated
                  ? 'border-gray-300 text-redhalo-navy bg-redhalo-mint/40 hover:bg-redhalo-mint/60'
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
              }`}
            >
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {showDeactivated ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                )}
              </svg>
              {showDeactivated ? 'Hide Inactive' : 'Show Inactive'}
            </button>

            {/* Formula Manager Button */}
            <button
              onClick={() => setShowFormulaManager(true)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-redhalo-red/35 transition-colors"
            >
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Formula Manager
            </button>
            
            <button
              onClick={handleCreateStructure}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-redhalo-red hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-redhalo-red/35"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create New Structure
            </button>
          </div>
        </div>

        {filteredStructures.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {structures.length === 0 ? 'No salary structures created yet' : 'No active salary structures'}
            </h3>
            <p className="text-gray-500 mb-6">
              {structures.length === 0 
                ? 'Start by creating your first salary structure using the components you\'ve defined'
                : 'All salary structures are currently inactive. Use the "Show Inactive" button to view them.'
              }
            </p>
            {structures.length === 0 && (
              <button
                onClick={handleCreateStructure}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-redhalo-red hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-redhalo-red/35"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Your First Structure
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredStructures.map((structure) => (
              <div 
                key={structure.id} 
                className={`border rounded-lg p-3 transition-all duration-200 hover:shadow-lg hover:scale-[1.02] ${
                  structure.active 
                    ? 'border-gray-200 bg-white' 
                    : 'border-gray-200 bg-gray-50 opacity-75'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-base font-medium text-gray-900">
                        {structure.name}
                      </h3>
                      {!structure.active && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Inactive
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>Components: {structure.components?.length || 0}</span>
                        <span>Type: {structure.salaryType === 'gross' ? 'Gross Salary' : 'CTC'}</span>
                        <span>Created: {formatDate(structure.createdAt)}</span>
                      </div>
                      
                      {structure.components && structure.components.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {structure.components.slice(0, 3).map((component, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium rh-accent-soft">
                              {component.name}
                            </span>
                          ))}
                          {structure.components.length > 3 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              +{structure.components.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* 3-Dot Menu */}
                  <div className="relative ml-4">
                    <button
                      onClick={() => handleMenuToggle(structure.id)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-all duration-200 hover:scale-110"
                      title="More options"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                      </svg>
                    </button>
                    
                    {/* Dropdown Menu */}
                    {openMenuId === structure.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10 animate-in slide-in-from-top-2 duration-200">
                        <div className="py-1">
                          <button
                            onClick={() => handleMenuAction('edit', structure.id, structure.name)}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150"
                          >
                            <svg className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                          </button>
                          
                          {structure.active ? (
                            <button
                              onClick={() => handleMenuAction('deactivate', structure.id, structure.name)}
                              className="flex items-center w-full px-4 py-2 text-sm text-yellow-700 hover:bg-yellow-50 hover:text-yellow-800 transition-colors duration-150"
                            >
                              <svg className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                              </svg>
                              Deactivate
                            </button>
                          ) : (
                            <button
                              onClick={() => handleMenuAction('reactivate', structure.id, structure.name)}
                              className="flex items-center w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50 hover:text-green-800 transition-colors duration-150"
                            >
                              <svg className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Reactivate
                            </button>
                          )}
                          
                          <button
                            onClick={() => handleMenuAction('delete', structure.id, structure.name)}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50 hover:text-red-800 transition-colors duration-150"
                          >
                            <svg className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Structure Creation Form Modal */}
      {showCreateForm && (
        <SalaryStructureForm
          isOpen={showCreateForm}
          onClose={handleCloseForm}
          onSubmit={handleSaveStructure}
          savedComponents={savedComponents}
          loading={loading}
          editingStructure={editingStructure}
          organizationalLevels={organizationalLevels}
          statutoryRates={statutoryRates}
        />
      )}

      {/* Formula Manager Modal */}
      {showFormulaManager && (
        <AdvancedFormulaManager
          isOpen={showFormulaManager}
          onClose={() => setShowFormulaManager(false)}
          savedComponents={savedComponents}
          organizationalLevels={organizationalLevels}
          statutoryRates={statutoryRates}
        />
      )}
    </div>
  );
};

export default SalaryStructureManager;

